export interface ChartSeries {
  readonly name: string;
  /** Numbers for chart geometry only. Never render these to the user. */
  readonly values: readonly number[];
  /** Display strings, one per value, carrying the exact formatted amount. */
  readonly labels: readonly string[];
}

/**
 * The categorical palette has a fixed number of validated slots and hues are never cycled or
 * generated. Anything past the last slot folds into a single "Other" series.
 *
 * Only valid when the series share a unit — transaction counts do, currencies do not. Summing THB and
 * USD into one line states a figure that means nothing; use `capToLimit` for those. `formatLabel` is
 * required rather than defaulted, because the summed value is read by a user through the chart's
 * accessible table, and a bare `String(sum)` would put an ungrouped, unitless number in front of them.
 */
export function foldToOther(
  series: readonly ChartSeries[],
  limit: number,
  formatLabel: (value: number) => string,
): readonly ChartSeries[] {
  if (limit < 2) throw new RangeError('foldToOther needs room for at least one slot plus Other');
  if (series.length <= limit) return series;

  const kept = series.slice(0, limit - 1);
  const remainder = series.slice(limit - 1);
  const length = Math.max(...remainder.map((entry) => entry.values.length));
  const values = Array.from({ length }, (_unused, index) =>
    remainder.reduce((total, entry) => total + (entry.values[index] ?? 0), 0),
  );

  return [...kept, { labels: values.map(formatLabel), name: 'Other', values }];
}

/**
 * Keeps the `limit` largest series, by total, and drops the rest from the plot. This is the fold for
 * series that do NOT share a unit: one line per currency can be compared by shape, but the amounts
 * cannot be added, so there is no honest "Other" line to draw.
 *
 * Dropping here means dropping from the *plot* only. `AreaTrendChart` still lists every original series
 * in its accessible table, so no currency is hidden from a screen-reader user.
 *
 * Ordering is by descending total, so the selection is deterministic and the largest series takes the
 * first palette slot. A series' colour therefore tracks its rank rather than its position in the input.
 */
export function capToLimit(series: readonly ChartSeries[], limit: number): readonly ChartSeries[] {
  if (limit < 1) throw new RangeError('capToLimit needs room for at least one series');
  if (series.length <= limit) return series;

  return [...series].sort((left, right) => seriesTotal(right) - seriesTotal(left)).slice(0, limit);
}

function seriesTotal(series: ChartSeries): number {
  return series.values.reduce((total, value) => total + value, 0);
}
